import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, PrimaryClaim, SecondaryCallout, EndpointDisplay, PageSubSection, PatientProfile, ComparisonTable, ReferencesBlock, KMCurve, TextBlock, FeaturedStat, NCCNcallout, ForestPlot } from '../../components';
import MIBC from '../../components/StudyDesign/__study-design/mibc';
import MIBCBFifteen from '../../components/StudyDesign/__study-design/mibc-b15';
import { jobCodes } from '../../util/data/jobCodes';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { NotationBlock, WYSIWYG } from '../../util/typography';
import { stringToSlug } from '../../util/functions';
 
// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-B15 and KEYNOTE-905 - Clinical Trial Results & Study Design`,
    keywords: `keynote 905, keynote-905`,
    description: `Health care professionals may find clinical trial results as well as study design for KEYNOTE-B15 and KEYNOTE-905 in certain adult patients with muscle invasive bladder cancer (MIBC).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/muscle-invasive-bladder-cancer-mibc/","@type":"ApprovedIndication","name":"KEYTRUDA and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated, in combination with enfortumab vedotin-ejfv (EV), as neoadjuvant treatment and then continued after cystectomy as adjuvant treatment for the treatment of adult patients with muscle invasive bladder cancer (MIBC)."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/muscle-invasive-bladder-cancer-mibc/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/muscle-invasive-bladder-cancer-mibc/","@type":"MedicalWebPage","name":"Muscle Invasive Bladder Cancer (MIBC)","description":"Perioperative Treatment of Cisplatin-Ineligible Adult Patients With Muscle Invasive Bladder Cancer (MIBC)"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/muscle-invasive-bladder-cancer-mibc/","@type":"MedicalTrial","name":"Clinical Findings From KEYNOTE-905","description":"Open-label, randomized, active-controlled, multicenter study of a neoadjuvant and adjuvant regimen of KEYTRUDA + EV and RC + PLND vs RC + PLND alone in cisplatin-ineligible patients with MIBC (N=344)","trialDesign":"RandomizedTrial"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/muscle-invasive-bladder-cancer-mibc/","@type":"MedicalTrial","name":"Clinical Findings From KEYNOTE-B15","description":"Phase 3, open-label, randomized, active-controlled, multicenter study of a neoadjuvant and adjuvant regimen of KEYTRUDA + EV and RC + PLND vs neoadjuvant gem + cis and RC + PLND in cisplatin-eligible patients with MIBC (N=808)","trialDesign":"RandomizedTrial"}`  
    ]
}
 
//from /src/util/data/indications.js
const indicationId = 40;
const jobCode = jobCodes[41].jobCode;
 
const PageSectionCustom = styled.div`
// > [class^='ComponentWrapper-']{
//     margin-bottom:0;
// }
// [class^='ComponentWrapper-'] [class^='ComponentWrapper-']:last-child{
//     margin-bottom:0;
// }
//     [class^='StudyDesign__MultiFlowWrapper-'] [class^='StudyDesign__MultiFlowItem-']:first-child [class^='Heading__Subtitle-']{
//     margin-top:0;
//     @media (max-width:767px){
//     margin-top:0;
//     }
// }
`;

 
const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];
 
const pageReferences = [
    {
        label: "1.",
        text: "Imfinzi. Prescribing information and medication guide. AstraZeneca Pharmaceuticals LP; 2025"
    },
    {
        label: "2.",
        text: "Opdivo. Prescribing information and medication guide. Bristol-Myers Squibb Company; 2026."
    },
    {
        label: "3.",
        text: "Tecentriq. Prescribing information and medication guide. Genentech, Inc; 2026."
    },
    {
        label: "4.",
        text: "Padcev. Prescribing information and patient information. Agensys, Inc. and Seagen Inc; 2025."
    },
    {
        label: "5.",
        text: "Libtayo. Prescribing information and medication guide. Regeneron Pharmaceuticals, Inc; 2025."
    },
    {
        label: "6.",
        text: "Bavencio. Prescribing information and medication guide. EMD Serono, Inc; 2025."
    },
    {
        label: "7.",
        text: "Vulsteke C, Adra N, Danchaivijitr P, et al. Perioperative enfortumab vedotin and pembrolizumab in bladder cancer. <i>N Engl J Med</i>. 2026;394(13):1257-1269. doi:10.1056/NEJMoa2511674"
    },
]
const crossLinkContent = {
    sections: [
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/muscle-invasive-bladder-cancer-mibc/'},
                { text: 'Dosing', url: '/dosing/options/#mibc' },
            ]
        },
    ],
    interestLinks: {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
}
 
const anchorLinkModelData = [
    { label: 'Clinical Findings From KEYNOTE&#8288;-&#8288;B15 Cisplatin-Eligible MIBC for KEYTRUDA + EV' },
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;B15 for KEYTRUDA' },
    { label: 'Clinical Findings From KEYNOTE&#8288;-&#8288;905 Cisplatin-Ineligible MIBC for KEYTRUDA + EV' },
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;905 for KEYTRUDA' },
]
 
const FsOsFootnote = [
    {
        label: "a.",
        text: "Based on stratified Cox regression model with Efron’s method of tie handling with treatment as a covariate."
    },
    {
        label: "b.",
        text: "Based on stratified log-rank test."
    },
    {
        label: "c.",
        text: "Based on Kaplan-Meier estimates."
    },
 
]
 
const FsEfsFootnote = [
    {
        label: "a.",
        text: "KEYNOTE-905 is also known as EV-303."
    },
    {
        label: "b.",
        text: "Based on stratified Cox regression model with Efron's method of tie handling with treatment as a covariate."
    },
    {
        label: "c.",
        text: "Based on stratified log-rank test."
    },
    {
        label: "d.",
        text: "Based on Kaplan-Meier estimates."
    }
 
]
 
const FsPcrFootnote = [
    {
        label: "a.",
        text: "Assessed by blinded independent pathology review."
    },
 
]
 
const EndPointOrDefinition = `CI = confidence interval; EFS = event-free survival; EV = enfortumab vedotin-ejfv; HR = hazard ratio; NR = not reached; OS = overall survival; pCR = pathological complete response; PLND = pelvic lymph node dissection; RC = radical cystectomy.`

const Page = ({ location }) => {
 
    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={pageMeta} location={location}>
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Clinical Trial Results" subtitle= "For KEYTRUDA" batch2 />
                    <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} />
                        <AnchorLinks items={anchorLinkModelData} />
                    </PageSection>
                    <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light">KEYTRUDA + EV Is the First Anti–PD-1 + Antibody-Drug Conjugate (ADC) Combination Indicated for the Perioperative Treatment of MIBC Regardless of Cisplatin Eligibility<sup>1-6</sup></PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}
 
                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                         <PageSection bgColor="cloud" title={'Clinical Findings From KEYNOTE&#8288;-&#8288;B15<sup>a</sup> Cisplatin-Eligible MIBC for KEYTRUDA + EV'} id={stringToSlug(anchorLinkModelData[0].label)}>
                            <KMCurve
                                title="Kaplan-Meier Curve Estimates of Event-Free Survival (EFS) in KEYNOTE&#8288;-&#8288;B15"
                                hr="HR<sup>b</sup>=0.53; (95% CI, 0.41–0.70; <i>P</i><sup>c</sup><0.0001)"
                                limitation='Superior EFS With Perioperative KEYTRUDA + EV vs Neoadjuvant Gem + Cis and RC + PLND'
                                image={() => <StaticImage
                                    placeholder="blurred"
                                    src="../../assets/KN-b15-km-efs-v2.webp"
                                    alt="Kaplan-Meier Estimates of Event-Free Survival in KEYNOTE-B15" />}
                                // footnotes={KmEfs905}
                                
                            />
 
                            <FeaturedStat
                                statistic="47%"
                                title="Reduction in the Risk of an EFS Event With Perioperative KEYTRUDA + EV vs Neoadjuvant Gem + Cis and RC + PLND"
                                hr="HR<sup>b</sup>=0.53; (95% CI, 0.41–0.70; <i>P</i><sup>c</sup><0.0001). <br/>Events observed: 87/405 (21%) with perioperative KEYTRUDA + EV vs 146/403 (36%) with neoadjuvant gem + cis and RC + PLND"
                            />
 
                            <SecondaryCallout
                                text={
                                    <WYSIWYG>
                                        <h4>Median EFS<sup>d</sup> (95% CI):</h4>
                                        <ul>
                                            <li><b>Perioperative KEYTRUDA + EV: NR (NR–NR)</b></li>
                                            <li><b>Neoadjuvant gem + cis and RC + PLND: 48.5 months (43.3–NR)</b></li>
                                        </ul>
                                    </WYSIWYG>
                                }
                                alt=""
                                limitation
                                additionalInformation={[
                                    {
                                        text: `EFS is defined as time from randomization to the first of: disease progression preventing curative surgery, failure to undergo surgery for participants with muscle invasive residual disease, incomplete surgical resection, local or distant recurrence after surgery, or death.`
                                    },
                                ]}
                                
                                definitions="CI = confidence interval; gem + cis = gemcitabine + cisplatin; HR = hazard ratio; NR = not reached; PD-1 = programmed death receptor-1; PLND = pelvic lymph node dissection; RC = radical cystectomy."
                                imgSrc={"/static/images/hcp-opt-callout-key-cut.svg"}
                            />
                            <KMCurve
                                title="Kaplan-Meier Estimates for Overall Survival in KEYNOTE-B15"
                                hr="HR<sup>b</sup>=0.65 (95% CI, 0.48–0.89; <i>P</i><sup>c</sup>=0.0029)"
                                limitation='Superior OS With Perioperative KEYTRUDA + EV vs Neoadjuvant Gem + Cis and RC + PLND'
                                image={() => <StaticImage
                                    placeholder="blurred"
                                    src="../../assets/KN-B15-km-os-v2.webp"
                                    alt="Kaplan-Meier Estimates of Overall Survival (OS) in KEYNOTE-B15" />}
                            />
 
                            <FeaturedStat
                                statistic="35%"
                                title="Reduction in the Risk of Death With Perioperative KEYTRUDA + EV vs Neoadjuvant Gem + Cis and RC + PLND"
                                hr="HR<sup>b</sup>=0.65 (95% CI, 0.48–0.89; <i>P</i><sup>c</sup>=0.0029). <br/>Events observed: 69/405 (17%) with perioperative KEYTRUDA + EV vs 99/403 (25%) with neoadjuvant gem + cis and RC + PLND"
                            />
                            <SecondaryCallout
                                text={
                                    <WYSIWYG>
                                        <h4>Median OS<sup>d</sup> (95% CI):</h4>
                                        <ul>
                                            <li><b>Perioperative KEYTRUDA + EV: NR (NR–NR)</b></li>
                                            <li><b>Neoadjuvant gem + cis and RC + PLND: NR (NR–NR)</b></li>
                                        </ul>
                                    </WYSIWYG>
                                }
                                alt=""
                                definitions="OS = overall survival."
                                imgSrc={"/static/images/hcp-opt-callout-key-cut.svg"}
                            />
                            <EndpointDisplay
                                title="Statistically Significant Difference in pCR Rate<sup>e</sup> in the KEYTRUDA + EV Arm vs the Neoadjuvant Gem + Cis Arm"
                                middleP="<0.0001"
                                onlyP={true}
                                cards={
                                    [
                                        {
                                            dataType: 'pCR rate',
                                            label: 'KEYTRUDA + EV and RC + PLND arm',
                                            rate: '55.8%',
                                            ci: '95% CI, 50.8–60.7',
                                            
                                        },
                                        {
                                            dataType: 'pCR rate',
                                            label: 'Neoadjuvant Gem + Cis and RC + PLND arm',
                                            rate: '32.5%',
                                            ci: '95% CI, 28.0–37.3',
                                            
                                        }
                                    ]
                                }
                                footnotes={[
                                    {
                                        label: "a.",
                                        text: "KEYNOTE-B15 is also known as EV-304."
                                    },
                                    {
                                        label: "b.",
                                        text: "Based on stratified Cox regression model with Efron's method of tie handling with treatment as a covariate."
                                    },
                                     {
                                        label: "c.",
                                        text: "One-sided <i>P</i> value based on stratified log-rank test."
                                    },
                                    {
                                        label: "d.",
                                        text: "Based on Kaplan-Meier estimates."
                                    },
                                
                                    {
                                        label: "e.",
                                        text: "Assessed by blinded independent pathology review."
                                    }
                                ]}
                                definitions="pCR = pathological complete response."
                            />
                        </PageSection>

                         <PageSection bgColor="white" title={anchorLinkModelData[1].label}>
                            <MIBCBFifteen />
                        </PageSection>

                        <PageSection bgColor="cloud" title={'Clinical Findings From KEYNOTE&#8288;-&#8288;905<sup>a</sup> Cisplatin-Ineligible MIBC for KEYTRUDA + EV'} id={stringToSlug(anchorLinkModelData[2].label)}>
                            <KMCurve
                                title="Kaplan-Meier Curve Estimates of Event-Free Survival in KEYNOTE&#8288;-&#8288;905"
                                hr="HR<sup>b</sup>=0.40; 95% CI, 0.28&#8211;0.57; <i>P</i><sup>c</sup><0.0001"
                                limitation='Superior EFS with perioperative KEYTRUDA + EV vs RC + PLND alone'
                                image={() => <StaticImage
                                    placeholder="blurred"
                                    src="../../assets/Km-efs-v2.png"
                                    alt="Kaplan-Meier Estimates of Event-Free Survival in KEYNOTE-905" />}
                            />
 
                            <FeaturedStat
                                statistic="60%"
                                title="Reduction in the Risk of an EFS Event With Perioperative KEYTRUDA + EV vs RC + PLND Alone"
                                hr="HR<sup>b</sup>=0.40 (95% CI: 0.28&#8211;0.57; <i>P</i><sup>c</sup><0.0001). <br/>Events observed: 48/170 (28%) with perioperative KEYTRUDA + EV vs 95/174 (55%) with RC + PLND alone."
                            />
 
                            <SecondaryCallout
                                text={
                                    <WYSIWYG>
                                        <h4>Median EFS<sup>d</sup> (95% CI):</h4>
                                        <ul>
                                            <li><b>Perioperative KEYTRUDA + EV: NR (37.3&#8211;NR)</b></li>
                                            <li><b>RC + PLND alone: 15.7 months (10.3&#8211;20.5)</b></li>
                                        </ul>
                                    </WYSIWYG>
                                }
                                alt=""
                                additionalInformation={[
                                    {
                                        text: `EFS is defined as time from randomization to the first of: disease progression preventing curative surgery, failure to undergo surgery for participants with muscle invasive residual disease, incomplete surgical resection, local or distant recurrence after surgery, or death.`                                       
                                    },
                                ]}
                                footnotes={FsEfsFootnote}
                                imgSrc={"/static/images/hcp-opt-callout-key-cut.svg"}
                            />
                            <KMCurve
                                title="Kaplan-Meier Estimates for Overall Survival in KEYNOTE-905"
                                hr="HR<sup>a</sup>=0.50; 95% CI, 0.33&#8211;0.74; <i>P</i><sup>b</sup>=0.0002"
                                limitation='Superior OS with perioperative KEYTRUDA + EV vs RC + PLND alone'
                                image={() => <StaticImage
                                    placeholder="blurred"
                                    src="../../assets/Km-os-v2.png"
                                    alt="Kaplan-Meier Estimates of Overall Survival (OS) in KEYNOTE-905" />}
                                // footnotes={FskmFootnote}
                            />
 
                            <FeaturedStat
                                statistic="50%"
                                title="Reduction in the Risk of Death With Perioperative KEYTRUDA + EV vs RC + PLND Alone"
                                hr="HR<sup>a</sup>=0.50 (95% CI: 0.33&#8211;0.74; <i>P</i><sup>b</sup>=0.0002). <br/>Events observed: 38/170 (22%) with perioperative KEYTRUDA + EV vs 68/174 (39%) with RC + PLND alone."
                            />
                            <SecondaryCallout
                                text={
                                    <WYSIWYG>
                                        <h4>Median OS<sup>c</sup> (95% CI):</h4>
                                        <ul>
                                            <li><b>Perioperative KEYTRUDA + EV: NR (NR–NR)</b></li>
                                            <li><b>RC + PLND alone: 41.7 months (31.8–NR)</b></li>
                                        </ul>
                                    </WYSIWYG>
                                }
                                alt=""
                                footnotes={FsOsFootnote}
                                imgSrc={"/static/images/hcp-opt-callout-key-cut.svg"}
                            />
                            <EndpointDisplay
                                title="Statistically Significant Difference in pCR Rate<sup>a</sup> in the KEYTRUDA + EV Arm vs the RC + PLND Alone Arm"
                                middleP="<0.0001"
                                onlyP={true}
                                cards={
                                    [
                                        {
                                            dataType: 'pCR rate',
                                            label: 'KEYTRUDA + EV and RC + PLND arm',
                                            rate: '57.1%',
                                            ci: '95% CI, 49.3&#8211;64.6',
                                            // N: '97/170',
                                        },
                                        {
                                            dataType: 'pCR rate',
                                            label: 'RC + PLND alone arm',
                                            rate: '8.6%',
                                            ci: '95% CI, 4.9&#8211;13.8',
                                            // N: '15/174',
                                        }
                                    ]
                                }
                                footnotes={FsPcrFootnote}
                            />
                        </PageSection>
 
 
                        <PageSection bgColor="white" title={anchorLinkModelData[3].label}>
                            <MIBC />
                        </PageSection>
 
                    </TemplateColumn>
                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>
 
                </TemplateColumnWithSidebarContainer>
 
                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>
                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};
 
export default Page;